import { useEffect, useRef } from 'react';
import * as Notifications from 'expo-notifications';
import { router } from 'expo-router';
import usePlatform from './usePlatform';

function useNotificationObserver(ready = true) {
    const { isWeb } = usePlatform();
    const lastHandledId = useRef(null);

    function redirect(notification, coldStart = false) {
        const data = notification.request.content.data;
        const path = data?.path;
        const notifId = data?.id || notification.request.identifier;
        if (!path) return;
        if (notifId && lastHandledId.current === notifId) return;
        lastHandledId.current = notifId;
        const target = path.startsWith('/') ? path : `/${path}`;
        // On cold start there's no existing stack, so an imperative push would make the
        // target the initial screen of its tab stack (modal presentation ignored, tab bar
        // shows, later nav corrupted). withAnchor forces the tab's anchor route beneath it.
        router.push(target, coldStart ? { withAnchor: true } : undefined);
        // A handled response must not replay (or suppress startup updates) later.
        // Check identity so clearing this tap cannot discard a newer one.
        try {
            const lastResponse = Notifications.getLastNotificationResponse();
            if (lastResponse?.notification?.request.identifier === notification.request.identifier) {
                Notifications.clearLastNotificationResponse();
            }
        } catch { /* Leaving the response cached is safe; routing already succeeded. */ }
    }

    useEffect(() => {
        if (isWeb || !ready) return;
        let isMounted = true;

        Notifications.getLastNotificationResponseAsync()
            .then(response => {
                if (!isMounted || !response?.notification) {
                    return;
                }
                redirect(response?.notification, true);
            });

        const subscription = Notifications.addNotificationResponseReceivedListener(response => {
            redirect(response.notification);
        });

        return () => {
            isMounted = false;
            subscription.remove();
        };
    }, [isWeb, ready]);
}

export default useNotificationObserver;
